import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import ListBulletedIcon from '@atlaskit/icon/core/list-bulleted';
import ClockIcon from '@atlaskit/icon/core/clock';
import CommentIcon from '@atlaskit/icon/core/comment';
import WorkItemsIcon from '@atlaskit/icon/core/work-items';
import AssetsIcon from '@atlaskit/icon/core/assets';
import AiGenerativeTextSummaryIcon from '@atlaskit/icon/core/ai-generative-text-summary';

import { type AIActionType, useSourceAIActions } from '@atlassian/ai-smart-button-api';

import {
	type ConversationAction,
	HandleActionType,
	useConversationActionsStoreActions,
} from '@confluence/conversation-assistant';
import { QuickSummaryStreamingComponent } from '@confluence/quick-summary';
import { useAutomationPermissionService } from '@confluence/automation-menu/entry-points/AutomationPermissionService';
import {
	CommentsSummarySource,
	FooterCommentsSummaryCombined,
} from '@confluence/ai-comments-summary';
import { PageCatchupComponent, FilterComponent } from '@confluence/page-catchup';
import {
	CommentCountsContext,
	CommentCountsProvider,
} from '@confluence/comment-context/entry-points/CommentCountsContext';
import { fg } from '@confluence/feature-gating';
import { useRightSidebarContext } from '@confluence/page-layout-context';
import {
	BULK_ISSUE_CREATE_SIDE_PANEL_ID,
	useWarningDialogStateActions,
	WarningDialog,
} from '@confluence/issue-create-side-panel';
import { ExploreRelatedWork } from '@confluence/explore-related-work';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import { PageMode } from '@confluence/page-utils/entry-points/enums';

import { CatchupContentType } from '../__types__/CatchupLastViewedTimeQuery';

import { useDynamicSplitButton } from './useDynamicSplitButton';
import { useBulkIssueCreate } from './useBulkIssueCreate';

export const i18n = defineMessages({
	inlineCommentsMenuItemLabel: {
		id: 'ai-floating-context-menu.menu.item.label.inline-comments',
		defaultMessage: 'Summarize comments',
		description: 'Label for the AI Content Actions Inline Comments Menu Item',
	},
	pageCatchupMenuItemLabel: {
		id: 'ai-floating-context-menu.menu.item.label.page-catchup',
		defaultMessage: 'Summarize changes',
		description: 'Label for the AI Content Actions Page Catchup Menu Item',
	},
	summarizeSectionLabel: {
		id: 'ai-floating-context-menu.summarize.section.label',
		defaultMessage: 'Summarize',
		description: 'Label for the summarize section in the menu group',
	},
	bulkIssueCreateMenuItemLabel: {
		id: 'ai-floating-context-menu.menu.item.label.bulk-issue-create',
		defaultMessage: 'Create Jira issues',
		description: 'Label for the Jira Bulk Issue Create Menu Item',
	},
	bulkWorkItemsCreateMenuItemLabel: {
		id: 'ai-floating-context-menu.menu.item.label.bulk-work-items-create',
		defaultMessage: 'Create Jira work items',
		description: 'Label for the Jira Bulk Work Items Create Menu Item',
	},
	exploreRelatedWorkAndUpdates: {
		id: 'ai-floating-context-menu.menu.item.label.explore-related-items.non-final',
		defaultMessage: 'Explore related work and resources',
		description: 'Label for the Explore Related Items Menu Item',
	},
});

const VIEW_PAGE_SOURCE_ID = 'view-page';

type AIConsumptionViewPageProps = {
	contentId: string;
	contentType: string;
	spaceKey: string;
	contentTitle: string;
	lastModifiedDate?: string | null;
};

export const useAIConsumptionViewPageRefreshed = ({
	contentId,
	contentType,
	spaceKey,
	contentTitle,
	lastModifiedDate,
}: AIConsumptionViewPageProps) => {
	const { formatMessage } = useIntl();
	const { setNudgesRefreshed } = useSourceAIActions(VIEW_PAGE_SOURCE_ID);
	const { setConversationActions } = useConversationActionsStoreActions();
	const { unresolvedInlineCommentsCount, footerCommentsCount } = useContext(CommentCountsContext);
	const pageMode = useGetPageMode();
	const { canManageAutomations } = useAutomationPermissionService(spaceKey);

	const rightSidebarContext = useRightSidebarContext();
	const { showWarningDialog } = useWarningDialogStateActions();
	const contentTypeEnum =
		contentType === 'page' ? CatchupContentType.PAGE : CatchupContentType.BLOGPOST;
	const { showCatchupAsDefault, showSummarizeAsDefault } = useDynamicSplitButton(
		contentId,
		contentTypeEnum,
	);
	const isSidePanelOpen =
		rightSidebarContext.getCurrent()?.panelId === BULK_ISSUE_CREATE_SIDE_PANEL_ID;

	const content = useRef<HTMLDivElement | null>(
		typeof document !== 'undefined' ? document.querySelector('div#content') : null,
	);

	const handleBulkIssueCreate = useBulkIssueCreate({
		contentId,
		content,
	});

	const pageCatchupAction = useMemo(
		() => ({
			id: 'summarize-changes-ai-action',
			title: formatMessage(i18n.pageCatchupMenuItemLabel),
			icon: () => <ClockIcon label="" />,
			viewConfig: {
				render: () => (
					<PageCatchupComponent
						contentId={contentId}
						contentType={contentType}
						spaceKey={spaceKey}
						setIsOpen={() => {}}
						contentPath={window.location.pathname}
						isInObjectSidebar
						showAIDisclaimer={false}
					/>
				),
				headerRightAlignedElement: fg('cc_ai_page_catchup_filtering') ? (
					<FilterComponent />
				) : undefined,
			},
			type: 'ai-action' as AIActionType,
			handleType: HandleActionType.Base,
		}),
		[formatMessage, contentId, contentType, spaceKey],
	);

	const quickSummaryAction = useMemo(
		() => ({
			id: 'summarize-page-ai-action',
			title: formatMessage(i18n.summarizeSectionLabel),
			icon: () => <ListBulletedIcon label="" />,
			viewConfig: {
				render: () => (
					<QuickSummaryStreamingComponent
						contentId={contentId}
						spaceKey={spaceKey}
						contentType={contentType}
						lastModifiedDate={lastModifiedDate ?? ''}
						source="view-page"
						setIsOpen={() => {}}
						contentPath={window.location.pathname}
						isInObjectSidebar
						showAIDisclaimer={false}
					/>
				),
			},
			type: 'ai-action' as AIActionType,
			handleType: HandleActionType.Base,
		}),
		[formatMessage, contentId, contentType, spaceKey, lastModifiedDate],
	);

	const onBulkIssueCreate = useCallback(() => {
		if (isSidePanelOpen) {
			showWarningDialog(WarningDialog.FLOATING);
		} else {
			void handleBulkIssueCreate();
		}
	}, [isSidePanelOpen, showWarningDialog, handleBulkIssueCreate]);

	const actions = useMemo(() => {
		const baseActions: ConversationAction[] = [quickSummaryAction, pageCatchupAction];

		if (unresolvedInlineCommentsCount > 0 || footerCommentsCount > 0) {
			baseActions.push({
				id: 'summarize-comments-ai-action',
				title: formatMessage(i18n.inlineCommentsMenuItemLabel),
				icon: () => <CommentIcon label="" />,
				viewConfig: {
					render: () => (
						<CommentCountsProvider contentId={contentId}>
							<FooterCommentsSummaryCombined
								contentId={contentId}
								contentType={contentType}
								source={CommentsSummarySource.Footer}
								spaceKey={spaceKey}
								isInObjectSidebar
								showAIDisclaimer={false}
							/>
						</CommentCountsProvider>
					),
				},
				type: 'ai-action' as AIActionType,
				handleType: HandleActionType.Base,
			});
		}

		if (pageMode === PageMode.VIEW && fg('confluence_ai_bulk_issue_creation')) {
			baseActions.push({
				id: 'bulk-issue-create-ai-action',
				title: formatMessage(
					fg('confluence-issue-terminology-refresh')
						? i18n.bulkWorkItemsCreateMenuItemLabel
						: i18n.bulkIssueCreateMenuItemLabel,
				),
				icon: () => <WorkItemsIcon label="" />,
				onClick: onBulkIssueCreate,
				type: 'ai-action' as AIActionType,
				handleType: HandleActionType.Base,
			});
		}

		if (fg('platform-linking-confluence-teamwork-context-panel')) {
			baseActions.unshift({
				id: 'explore-related-work',
				title: formatMessage(i18n.exploreRelatedWorkAndUpdates),
				icon: () => <AssetsIcon label="" />,
				viewConfig: {
					render: () => (
						<ExploreRelatedWork
							contentId={contentId}
							contentTitle={contentTitle}
							showFooter={false}
						/>
					),
				},
				type: 'ai-action' as AIActionType,
				handleType: HandleActionType.Base,
			});
		}

		return baseActions;
	}, [
		formatMessage,
		contentId,
		contentTitle,
		spaceKey,
		contentType,
		unresolvedInlineCommentsCount,
		footerCommentsCount,
		quickSummaryAction,
		pageCatchupAction,
		onBulkIssueCreate,
		pageMode,
	]);

	useEffect(() => {
		const isPageOrBlogpost = contentType === 'page' || contentType === 'blogpost';
		setConversationActions(isPageOrBlogpost ? actions : []); // needed for space overview since it doesn't have content type

		if (showSummarizeAsDefault && isPageOrBlogpost) {
			setNudgesRefreshed([
				{
					...quickSummaryAction,
					icon: () => <AiGenerativeTextSummaryIcon label="" />,
				},
			]);
		}

		if (showCatchupAsDefault && isPageOrBlogpost) {
			setNudgesRefreshed([
				{
					...pageCatchupAction,
					icon: () => <ClockIcon label="" />,
				},
			]);
		}

		return () => {
			setConversationActions([]);
		};
	}, [
		actions,
		pageCatchupAction,
		showCatchupAsDefault,
		contentType,
		spaceKey,
		contentId,
		contentTitle,
		canManageAutomations,
		quickSummaryAction,
		showSummarizeAsDefault,
		setConversationActions,
		setNudgesRefreshed,
	]);
};
